package org.example.project.engineslot

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.dashboard.PopupEngineCard
import org.example.project.service.currentSaveArt
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun SlotSetup(art: String = currentSaveArt()) {
    var selectedDate by remember { mutableStateOf(slotDefaultDate()) }
    var config by remember { mutableStateOf(slotDefaultConfig()) }
    var snapCompanies by remember { mutableStateOf<List<All>>(emptyList()) }
    var companyContext by remember { mutableStateOf(SlotCompanyContext("", "", "", "")) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var masterData by remember { mutableStateOf<List<All>>(emptyList()) }
    var zugmaschinen by remember { mutableStateOf<List<All>>(emptyList()) }
    var auflieger by remember { mutableStateOf<List<All>>(emptyList()) }
    var bookings by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var selectedCell by remember { mutableStateOf<All?>(null) }
    var bookingPopup by remember { mutableStateOf<All?>(null) }
    var bookingPopupOriginal by remember { mutableStateOf<All?>(null) }
    var pendingEndToEndTarget by remember { mutableStateOf<All?>(null) }
    var pendingEndToEndConfirm by remember { mutableStateOf<All?>(null) }
    var detailPopup by remember { mutableStateOf<All?>(null) }
    var calendarOpen by remember { mutableStateOf(false) }
    var hoverBooking by remember { mutableStateOf<All?>(null) }
    var hoverX by remember { mutableStateOf(240.0) }
    var hoverY by remember { mutableStateOf(140.0) }
    var setupInitialized by remember { mutableStateOf(false) }
    var contextLoadToken by remember { mutableStateOf(0) }
    var contextLoading by remember { mutableStateOf(false) }
    var configLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val slotLiveVersion = LocalScreenRefreshVersion.current

    fun coerceSetupDate(targetDate: String): String {
        val today = slotDefaultDate()
        return if (targetDate < today) today else targetDate
    }

    suspend fun loadCompanyContext(
        targetFirmenId: String,
        keepStandort: String = "",
        companyOptions: List<All> = snapCompanies
    ) {
        val loadToken = contextLoadToken + 1
        contextLoadToken = loadToken
        contextLoading = true
        try {
            val resolvedContext = SlotSnapCompanyApi.resolveContext(companyOptions, targetFirmenId)
            val loadedStandorte = SlotStammdatenApi.loadStandortQuellen(resolvedContext)
            val loadedBookings = loadSlotBookings(resolvedContext.firmenId)
            val nextContext = resolvedContext.copy(
                label = resolvedContext.label.ifBlank {
                    loadedStandorte.firstOrNull()?.firma.orEmpty().ifBlank { resolvedContext.firmenId }
                }
            )
            val standortOptions = slotStandortOptions(loadedStandorte)
            val nextStandort = standortOptions.firstOrNull { it.key == keepStandort }?.key
                ?: standortOptions.firstOrNull()?.key.orEmpty()
            val nextConfig = if (nextStandort.isNotBlank()) {
                loadSlotConfig(resolvedContext.firmenId, nextStandort).copy(standort = nextStandort)
            } else {
                slotDefaultConfig()
            }
            if (loadToken != contextLoadToken) return
            companyContext = nextContext
            standorte = loadedStandorte
            bookings = loadedBookings
            selectedStandort = nextStandort
            selectedCell = null
            config = nextConfig
        } finally {
            if (loadToken == contextLoadToken) {
                contextLoading = false
            }
        }
    }

    fun endToEndReference(booking: All): String =
        slotLadenTouren(booking).firstOrNull()?.lieferscheinnummer.orEmpty()
            .ifBlank { booking.lieferscheinnummer }

    fun matchingSavedEndToEndBooking(savedBookings: List<All>, candidate: All): All? {
        val reference = endToEndReference(candidate)
        if (reference.isBlank()) return null
        return savedBookings.firstOrNull { saved ->
            saved.endToEndFlow &&
                saved.flowTargetFirmenId.isNotBlank() &&
                slotBookingArtCode(saved) == SLOT_BEREICH_WA &&
                endToEndReference(saved) == reference
        }
    }

    fun targetWeBooking(cell: All, source: All): All {
        val sourceTour = slotLadenTouren(source).firstOrNull()
        val reference = source.flowTargetWeLieferscheinnummer
            .ifBlank { sourceTour?.lieferscheinnummer.orEmpty() }
            .ifBlank { source.lieferscheinnummer }
        val target = cell.copy(
            firmenId = source.flowTargetFirmenId,
            firma = companyContext.label,
            auftragsart = SLOT_BEREICH_WE,
            slotBereich = SLOT_BEREICH_WE,
            lieferscheinnummer = reference,
            lieferant = sourceTour?.lieferant.orEmpty().ifBlank { source.lieferant },
            empfaenger = sourceTour?.empfaenger.orEmpty().ifBlank { source.empfaenger },
            auftraggeber = sourceTour?.auftraggeber.orEmpty().ifBlank { source.auftraggeber },
            unternehmer = sourceTour?.unternehmer.orEmpty().ifBlank { source.unternehmer },
            fahrername = sourceTour?.fahrername.orEmpty().ifBlank { source.fahrername },
            lkwkennzeichen = sourceTour?.lkwkennzeichen.orEmpty().ifBlank { source.lkwkennzeichen },
            aufliegerkennzeichen = sourceTour?.aufliegerkennzeichen.orEmpty().ifBlank { source.aufliegerkennzeichen },
            slotDriverUserId = sourceTour?.slotDriverUserId.orEmpty().ifBlank { source.slotDriverUserId },
            palettenanzahl = 0,
            yardabschlussIstPaletten = 0,
            yardabschlussOffenPaletten = 0,
            lieferantFirmenId = source.lieferantFirmenId,
            empfaengerFirmenId = source.empfaengerFirmenId,
            auftraggeberFirmenId = source.auftraggeberFirmenId,
            unternehmerFirmenId = source.unternehmerFirmenId,
            endToEndFlow = true,
            flowWeAvis = false,
            flowTyp = "wa_to_we",
            flowStatus = "",
            flowId = source.flowId,
            flowSourceFirmenId = source.firmenId,
            flowSourceWaTour = source.lieferscheinnummer,
            flowSourceWaLkwId = source.id.ifBlank { source.lfdNr.takeIf { value -> value > 0 }?.toString().orEmpty() },
            flowTargetFirmenId = source.flowTargetFirmenId,
            flowTargetWeId = source.flowTargetWeId,
            flowTargetWeLieferscheinnummer = reference
        )
        return target.copy(
            slotLiefernBelege = Json.encodeToString(
                ListSerializer(All.AllNames.serializer()),
                listOf(slotLiefernDefaultBeleg(target))
            )
        )
    }

    fun openEndToEndTargetBooking(source: All) {
        if (!source.endToEndFlow || source.flowTargetFirmenId.isBlank()) return
        scope.launch {
            pendingEndToEndTarget = source
            bookingPopupOriginal = null
            bookingPopup = null
            selectedCell = null
            selectedDate = coerceSetupDate(source.datum)
            loadCompanyContext(source.flowTargetFirmenId, companyOptions = snapCompanies)
        }
    }

    fun openBookingForCell(cell: All) {
        scope.launch {
            masterData = runCatching { loadSlotBookingMasterData() }.getOrDefault(masterData)
            val currentCell = bookings.firstOrNull { slotSameCell(it, cell) } ?: cell
            bookingPopupOriginal = currentCell.copy()
            val baseBooking = currentCell.copy(
                firmenId = companyContext.firmenId,
                firma = companyContext.label
            )
            val pending = pendingEndToEndTarget
            bookingPopup = if (
                pending != null &&
                pending.flowTargetFirmenId == companyContext.firmenId &&
                !slotHasBookingData(currentCell)
            ) {
                targetWeBooking(baseBooking, pending)
            } else {
                baseBooking
            }
        }
    }

    LaunchedEffect(art) {
        val loadedSnapCompanies = SlotSnapCompanyApi.loadSelectableCompanies()
        snapCompanies = loadedSnapCompanies
        masterData = loadSlotBookingMasterData()
        zugmaschinen = loadSlotMeineZugmaschinen()
        auflieger = loadSlotMeineAuflieger()
        val ownFirmenId = SlotSnapCompanyApi.currentFirmenId()
        loadCompanyContext(ownFirmenId, companyOptions = loadedSnapCompanies)
        setupInitialized = true
    }

    LaunchedEffect(slotLiveVersion) {
        if (!setupInitialized || slotLiveVersion == 0L) return@LaunchedEffect
        if (contextLoading || configLoading) return@LaunchedEffect
        val currentFirmenId = companyContext.firmenId.ifBlank { SlotSnapCompanyApi.currentFirmenId() }
        val currentStandort = selectedStandort
        snapCompanies = SlotSnapCompanyApi.loadSelectableCompanies()
        masterData = loadSlotBookingMasterData()
        zugmaschinen = loadSlotMeineZugmaschinen()
        auflieger = loadSlotMeineAuflieger()
        standorte = SlotStammdatenApi.loadStandortQuellen(companyContext)
        bookings = loadSlotBookings(currentFirmenId)
        if (currentStandort.isNotBlank()) {
            config = loadSlotConfig(currentFirmenId, currentStandort).copy(standort = currentStandort)
        }
        selectedCell?.let { current ->
            selectedCell = bookings.firstOrNull { it.id == current.id } ?: current
        }
        detailPopup?.let { current ->
            detailPopup = bookings.firstOrNull { it.id == current.id } ?: current
        }
    }

    LaunchedEffect(selectedDate, selectedStandort, companyContext.firmenId) {
        if (contextLoading || configLoading) return@LaunchedEffect
        val yardFirmenId = companyContext.firmenId.trim()
        if (yardFirmenId.isBlank() || selectedStandort.isBlank()) return@LaunchedEffect
        val loaded = loadSlotBookings(yardFirmenId)
        bookings = loaded
        selectedCell?.let { current ->
            selectedCell = loaded.firstOrNull { it.id == current.id } ?: current
        }
    }

    SlotSetupLayout(
        selectedFirmaLabel = companyContext.label,
        snapOptions = snapCompanies,
        selectedSnapKey = companyContext.firmenId,
        selectedDate = selectedDate,
        selectedStandort = selectedStandort,
        selectedCell = selectedCell,
        config = config,
        standorte = standorte,
        bookings = bookings,
        calendarOpen = calendarOpen,
        loading = contextLoading || configLoading,
        onConfigChange = {
            config = it
            scope.launch {
                saveSlotConfig(it, companyContext.firmenId, selectedStandort)
            }
        },
        onSnapChange = { selected ->
            scope.launch {
                if (selected.isBlank()) {
                    companyContext = SlotCompanyContext("", "", "", "")
                    standorte = emptyList()
                    bookings = emptyList()
                    selectedStandort = ""
                    selectedCell = null
                    config = slotDefaultConfig()
                } else {
                    loadCompanyContext(selected)
                }
            }
        },
        onToggleCalendar = { calendarOpen = !calendarOpen },
        onStandortChange = { selected ->
            selectedStandort = selected
            selectedCell = null
            configLoading = true
            scope.launch {
                try {
                    config = if (selected.isBlank()) {
                        slotDefaultConfig()
                    } else {
                        loadSlotConfig(companyContext.firmenId, selected).copy(standort = selected)
                    }
                } finally {
                    configLoading = false
                }
            }
        },
        onDateChange = { selected ->
            selectedDate = coerceSetupDate(selected)
            selectedCell = null
            hoverBooking = null
            calendarOpen = false
        },
        onSlotSelect = { cell, booking ->
            selectedCell = booking ?: cell
            val pending = pendingEndToEndTarget
            if (
                booking == null &&
                pending != null &&
                pending.flowTargetFirmenId == companyContext.firmenId
            ) {
                openBookingForCell(cell)
            }
        },
        onSlotDoubleClick = { _, booking ->
            if (booking != null) {
                if (slotBookingArtCode(booking) == SLOT_BEREICH_WA || slotBookingArtCode(booking) == SLOT_BEREICH_WE) {
                    bookingPopupOriginal = booking.copy()
                    bookingPopup = booking.copy(
                        firmenId = companyContext.firmenId,
                        firma = companyContext.label
                    )
                } else {
                    detailPopup = booking
                }
            }
        },
        onBookingHover = { booking, x, y ->
            hoverBooking = booking
            val (nextX, nextY) = slotHoverOverlayPosition(booking, x, y)
            hoverX = nextX
            hoverY = nextY
        },
        onBookingHoverEnd = { hoverBooking = null },
        onOpenBooking = {
            val cell = selectedCell ?: return@SlotSetupLayout
            openBookingForCell(cell)
        }
    )

    bookingPopup?.let { booking ->
        SlotSetupBookingPopup(
            booking = booking,
            masterData = masterData,
            standorte = standorte,
            zugmaschinen = zugmaschinen,
            auflieger = auflieger,
            onClose = {
                bookingPopupOriginal = null
                bookingPopup = null
            },
            onDelete = {
                scope.launch {
                    val original = bookingPopupOriginal
                    if (original != null && (slotHasBookingData(original) || slotCellState(original) != org.example.project.service.ZustandManager.Slot.FREI)) {
                        runCatching {
                            deleteSlotBooking(original)
                        }.onSuccess {
                            bookings = loadSlotBookings(companyContext.firmenId)
                            bookingPopupOriginal = null
                            bookingPopup = null
                        }.onFailure { error ->
                            AppDialogs.alert(error.message ?: "Slot kann nicht gelöscht werden.")
                        }
                        return@launch
                    }
                    bookingPopupOriginal = null
                    bookingPopup = null
                }
            },
            onBookTargetSlot = { source ->
                openEndToEndTargetBooking(source)
            },
            onSave = {
                scope.launch {
                    runCatching {
                        saveSlotBooking(
                            it.copy(
                                zustand = slotBookingStateForSave(it)
                            )
                        )
                    }.onSuccess { savedBookings ->
                        bookings = savedBookings
                        if (slotBookingArtCode(it) == SLOT_BEREICH_WE && it.endToEndFlow && it.flowId.isNotBlank()) {
                            pendingEndToEndTarget = null
                        }
                        val savedEndToEnd = matchingSavedEndToEndBooking(savedBookings, it)
                        bookingPopupOriginal = null
                        bookingPopup = null
                        if (savedEndToEnd != null) {
                            pendingEndToEndConfirm = savedEndToEnd
                        }
                    }.onFailure { error ->
                        AppDialogs.alert(error.message ?: "Slot konnte nicht gespeichert werden.")
                    }
                }
            }
        )
    }

    pendingEndToEndConfirm?.let { source ->
        PopupEngineCard(
            windowKey = "slot-end-to-end-confirm-${source.flowId.ifBlank { source.lieferscheinnummer }}",
            widthPx = 520,
            heightPx = 260,
            minWidthPx = 480,
            minHeightPx = 240,
            initialX = 420.0,
            initialY = 180.0,
            resizable = false,
            onClose = { pendingEndToEndConfirm = null }
        ) {
            Div({
                style {
                    width(100.percent)
                    height(100.percent)
                    padding(24.px)
                    boxSizing("border-box")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    gap(22.px)
                }
            }) {
                Span({
                    style {
                        fontSize(18.px)
                        fontWeight("700")
                        textAlign("center")
                        lineHeight("1.35")
                    }
                }) {
                    Text("Der Zielort ist ebenfalls ein Lumatica Kunde. Direkt die Lieferung anmelden?")
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(14.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    LumaticaButton(
                        label = "Nein",
                        onClick = { pendingEndToEndConfirm = null },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42,
                        paddingH = 28
                    )
                    LumaticaButton(
                        label = "Ja",
                        onClick = {
                            pendingEndToEndConfirm = null
                            openEndToEndTargetBooking(source)
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42,
                        paddingH = 34
                    )
                }
            }
        }
    }

    hoverBooking?.let { booking ->
        SlotBookingHoverOverlay(
            booking = booking,
            masterData = masterData,
            standorte = standorte,
            x = hoverX,
            y = hoverY
        )
    }

    detailPopup?.let { booking ->
        SlotBookingInfoPopup(
            booking = booking,
            masterData = masterData,
            standorte = standorte,
            onClose = { detailPopup = null }
        )
    }
}
